import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectorRef } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import {
  RawMaterial,
  LabourItem,
  FixedItem,
  VariableOverhead,
  Competitor,
  IndirectLabourItem,
} from '../raw-materials-component/raw-materials.component';

interface DirectLaborDto {
  activity: string;
  monthlySalary: number;
  hoursWorkedPm: number;
  ratePerMinute: number;
  minutesPerJob: number;
  unitCost: number;
  employeeName: string;
  roleDescription: string;
  rateType: string;
}

interface IndirectLaborDto {
  activity: string;
  monthlySalary: number;
  hoursWorkedPm: number;
  ratePerMinute: number;
  minutesPerActivity: number;
  unitCost: number;
  roleDescription: string;
  rateType: string;
}

interface FixedOverheadDto {
  itemName: string;
  costPerMonth: number;
  hoursOpenPerMonth: number;
  ratePerMinute: number;
  minutesPerJob: number;
  unitCost: number;
}

interface VariableOverheadDto {
  itemName: string;
  monthlyCost: number;
  estimatedSales: number;
  purchasePrice: number;
  purchaseQuantity: number;
  purchaseUnit: string;
  usageQuantity: number;
  usageUnit: string;
  batchSize: number;
  purchasePricePerUnit: number;
  unitCost: number;
}
interface SubscriptionDetails {
  id: number;
  tenantId: number;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  status: string;
  currentPeriodStart: string; // Keep as string for initial fetch
  currentPeriodEnd: string;
  planId: string;
  planName: string;
  amount: number;
  currency: string;
  isCancelled: boolean;
  cancelledAt: null;
  createdAt: string;
  updatedAt: string;
  tokenLimit: number;
  tenant: null;
}

interface RawMaterialDto {
  itemName: string;
  purchaseQuantity: number;
  purchasePrice: number;
  purchasePricePerUnit: number;
  usageQuantity: number;
  purchaseUnit: string;
  usageUnit: string;
  batchSize: number;
  unitCost: number;
}

interface CompetitorDto {
  name: string;
  price: number;
}

@Component({
  selector: 'app-aivor-insights',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './aivor-insights.component.html',
  styleUrls: ['./aivor-insights.component.css'],
})
export class AivorInsightsComponent implements OnInit, OnChanges {
  @Input() tenantProfile: any;
  @Input() productName: string = '';
  @Input() directMaterials: RawMaterial[] = [];
  @Input() directLabor: LabourItem[] = [];
  @Input() indirectLabor: IndirectLabourItem[] = [];
  @Input() fixedOverheads: FixedItem[] = [];
  @Input() variableOverheads: VariableOverhead[] = [];
  @Input() competitors: Competitor[] = [];
  @Input() mobileMode: boolean = false;

  recommendations: string[] = [];
  suggestedPrice: number = 0;
  AImarkupPrice: number = 0;
  IsLoading = false;
  errorMessage: string | null = null;

  // New property to store the total tokens
  totalTokensConsumed: number | null = null;
  tokenLimit: number = 0;
  tokensRemaining: number | null = null; // New property for remaining tokens

  @Output() suggestedPriceChange = new EventEmitter<number>();
  @Output() markupPriceChange = new EventEmitter<number>();

  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.fetchSubscriptionAndTokens();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tenantProfile'] && this.tenantProfile) {
      this.handleProcessPrompt();
    }
  }
  private fetchSubscriptionAndTokens(): void {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) {
      console.warn('Auth token not found. Cannot fetch data.');
      return;
    }

    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    this.http
      .get<SubscriptionDetails>(
        `${environment.apiUrl}/api/Subscription/GetCurrent`,
        { headers }
      )
      .subscribe({
        next: (subscription) => {
          if (subscription && subscription.currentPeriodStart) {
            // **SECURITY FIX:** Do not hardcode tokenLimit. Fetch it from the server.
            this.tokenLimit = subscription.tokenLimit;
            const periodStartDate = new Date(
              subscription.currentPeriodStart
            ).toISOString();

            this.http
              .get<number>(
                `${environment.apiUrl}/api/ai/total-tokens-consumed?currentPeriodStart=${periodStartDate}`,
                { headers }
              )
              .subscribe({
                next: (tokens) => {
                  this.totalTokensConsumed = tokens;
                  // **LOGIC FIX:** Ensure tokensRemaining doesn't go below zero
                  this.tokensRemaining = Math.max(
                    0,
                    this.tokenLimit - (this.totalTokensConsumed || 0)
                  );
                  this.cdr.detectChanges();
                },
                error: (error) => {
                  console.error(
                    'Error fetching total tokens for current period:',
                    error
                  );
                  this.totalTokensConsumed = null;
                  this.tokensRemaining = null;
                },
              });
          } else {
            console.error('Subscription details or start date not found.');
            this.totalTokensConsumed = 0;
            this.tokensRemaining = 0;
          }
        },
        error: (error) => {
          console.error('Error fetching subscription details:', error);
          this.totalTokensConsumed = null;
          this.tokensRemaining = null;
        },
      });
  }

  private getDirectLaborDtos(): DirectLaborDto[] {
    return this.directLabor.map((dl) => ({
      activity: dl.activity,
      monthlySalary: dl.monthlySalary,
      hoursWorkedPm: dl.hoursWorkedPm,
      ratePerMinute: dl.ratePerMinute,
      minutesPerJob: dl.minutesPerJob,
      unitCost: dl.unitCost,
      batchSize: dl.batchSize,
      employeeName: dl.employeeName,
      roleDescription: dl.roleDescription,
      rateType: dl.rateType,
    }));
  }

  private getIndirectLaborDtos(): IndirectLaborDto[] {
    return this.indirectLabor.map((il) => ({
      activity: il.activity,
      monthlySalary: il.monthlySalary,
      hoursWorkedPm: il.hoursWorkedPm,
      ratePerMinute: il.ratePerMinute,
      minutesPerActivity: il.minutesPerActivity,
      unitCost: il.unitCost,
      batchSize: il.batchSize,
      roleDescription: il.roleDescription,
      rateType: il.rateType,
    }));
  }

  private getVariableOverheadDtos(): VariableOverheadDto[] {
    return this.variableOverheads.map((vo) => ({
      itemName: vo.itemName?.trim() || '',
      monthlyCost: vo.monthlyCost,
      estimatedSales: vo.estimatedSales,
      purchasePrice: vo.purchasePrice ?? 0,
      purchaseQuantity: vo.purchaseQuantity ?? 0,
      purchaseUnit: vo.purchaseUnit?.trim() || '',
      usageQuantity: vo.usageQuantity ?? 0,
      usageUnit: vo.usageUnit?.trim() || '',
      batchSize: vo.batchSize ?? 0,
      purchasePricePerUnit: vo.purchasePricePerUnit ?? 0,
      unitCost: vo.unitCost ?? 0,
    }));
  }

  private getRawMaterialDtos(): RawMaterialDto[] {
    return this.directMaterials.map((rm) => ({
      itemName: rm.itemName?.trim() || '',
      purchaseQuantity: rm.purchaseQuantity,
      purchasePrice: rm.purchasePrice,
      purchasePricePerUnit: rm.purchasePricePerUnit,
      usageQuantity: rm.usageQuantity,
      purchaseUnit: rm.purchaseUnit?.trim() || '',
      usageUnit: rm.usageUnit?.trim() || '',
      batchSize: rm.batchSize,
      unitCost: rm.unitCost,
    }));
  }

  private getFixedOverheadDtos(): FixedOverheadDto[] {
    return this.fixedOverheads.map((fh) => ({
      itemName: fh.itemName,
      costPerMonth: fh.costPerMonth,
      hoursOpenPerMonth: fh.hoursOpenPerMonth,
      ratePerMinute: fh.ratePerMinute,
      batchSize: fh.batchSize,
      minutesPerJob: fh.minutesPerOrder,
      unitCost: fh.unitCost,
    }));
  }

  private getCompetitorDtos(): CompetitorDto[] {
    return this.competitors.map((c) => ({
      name: c.name,
      price: c.price,
    }));
  }

  getTotalCost(): number {
    const materialCost = this.directMaterials.reduce(
      (sum, item) => sum + (item.unitCost || 0),
      0
    );
    const directLaborCost = this.directLabor.reduce(
      (sum, item) => sum + (item.unitCost || 0),
      0
    );
    const indirectLaborCost = this.indirectLabor.reduce(
      (sum, item) => sum + (item.unitCost || 0),
      0
    );
    const fixedOverheadCost = this.fixedOverheads.reduce(
      (sum, item) => sum + (item.unitCost || 0),
      0
    );
    const variableOverheadCost = this.variableOverheads.reduce(
      (sum, item) => sum + (item.unitCost || 0),
      0
    );

    return (
      materialCost +
      directLaborCost +
      fixedOverheadCost +
      variableOverheadCost +
      indirectLaborCost
    );
  }

  async handleProcessPrompt(): Promise<void> {


    // **CLIENT-SIDE CHECK:** This check is for user experience only.
    // The server must perform its own, authoritative check.
    if (!this.tenantProfile) { return; }

    if (this.tokensRemaining !== null && this.tokensRemaining <= 0) {
      this.recommendations = [
        'Your monthly token limit has been reached. Please wait for the next billing cycle to use Aivor again.',
      ];
      this.cdr.detectChanges();
      return;
    }

    this.IsLoading = true;
    this.errorMessage = null;
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    const requestBody = {
      businessDetails: {
        name: this.tenantProfile.tenantProfileName?.trim() || '',
        industry: this.tenantProfile.industry?.trim() || '',
        businessModel: this.tenantProfile.businessModels?.trim() || '',
        location: this.tenantProfile.location?.trim() || '',
        businessType: this.tenantProfile.businessType?.trim() || '',
        description: this.tenantProfile.businessBrief?.trim() || '',
        size: this.tenantProfile.businessSize?.trim() || '',
        targetMarket: this.tenantProfile.targetMarkets?.trim() || '',
        vendorType: this.tenantProfile.vendorType?.trim() || '',
        salesChannels: this.tenantProfile.salesChannels?.trim() || '',
        keyChallenges: this.tenantProfile.keyChallenges?.trim() || '',
      },
      productDetails: {
        productName: this.productName?.trim() || '',
      },
      costData: {
        materials: this.getRawMaterialDtos(),
        directLabor: this.getDirectLaborDtos(),
        indirectLabor: this.getIndirectLaborDtos(),
        fixedOverheads: this.getFixedOverheadDtos(),
        variableOverheads: this.getVariableOverheadDtos(),
        competitors: this.getCompetitorDtos(),
      },
      dashboard: { dashboardName: 'costCalculator' },
      profitMargin: {
        lowerRange: 0.25,
        upperRange: 0.55,
        totalCost: this.getTotalCost(),
      },
      budget: {
        budgetItems: [],
      },
    };
    try {
      const response = await fetch(
        `${environment.apiUrl}/api/ai/process-prompt`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        // **SERVER-SIDE VALIDATION:**
        // Check for a specific status code from your backend that indicates a token limit has been reached.
        // For example, if your backend returns a 403 Forbidden or a custom 4xx error with a specific message.
        if (response.status === 403) {
          const errorBody = await response.json();
          if (
            errorBody.message &&
            errorBody.message.includes('Token limit reached')
          ) {
            this.recommendations = [
              'Your monthly token limit has been reached. Please wait for the next billing cycle to use Aivor again.',
            ];
            this.IsLoading = false;
            this.cdr.detectChanges();
            return;
          }
        }
        let serverMessage: string | null = null;
        try {
          const errorBody = await response.json();
          serverMessage = errorBody?.message || null;
        } catch {
          // Response body wasn't JSON; fall back to the generic message below.
        }
        throw new Error(
          serverMessage || `Server error: ${response.status} - ${response.statusText}`
        );
      }

      const data = (await response.json()) as {
        fromCache: boolean;
        recommendations: string[];
        sellingPrice: number;
        promptTokens?: number;
        completionTokens?: number;
        totalTokens?: number;
      };

      if (!Array.isArray(data.recommendations)) {
        throw new Error('Invalid response: recommendations is not an array');
      }

      this.recommendations = data.recommendations
        .map((rec: string | string[]) => {
          if (typeof rec === 'string') {
            if (rec.match(/[.?!]\s+[A-Z]/)) {
              return rec.split(/(?<=[.?!])\s+(?=[A-Z])/);
            }
            return [rec];
          }
          return rec;
        })
        .flat();
      this.suggestedPrice = data.sellingPrice;
      this.AImarkupPrice = this.suggestedPrice - this.getTotalCost();

      this.suggestedPriceChange.emit(this.suggestedPrice);
      this.markupPriceChange.emit(this.AImarkupPrice);

      this.fetchSubscriptionAndTokens();

      this.cdr.detectChanges();
    } catch (error: any) {
      console.error('Error calling AI API:', error);
      // Clear any previous successful response — otherwise a failed request just
      // leaves the last recommendation on screen, looking like Aivor "didn't change".
      this.recommendations = [];
      this.suggestedPrice = 0;
      this.AImarkupPrice = 0;
      this.errorMessage = error?.message || 'Something went wrong while asking Aivor. Please try again.';
      this.cdr.detectChanges();
    } finally {
      this.IsLoading = false;
    }
  }

  retry(): void {
    this.handleProcessPrompt();
  }
}
